import React, { useEffect, useState } from 'react'
import { useHistory, useParams, useRouteMatch } from 'react-router-dom'

import Grid from '@mui/material/Grid'

import { observer } from 'mobx-react-lite'
import { Instance } from 'mobx-state-tree'
import { Add } from '@mui/icons-material'

import { Topic, TopicHelper, useStores } from '@trivie/core'
import { Button } from '@trivie/ui-web/components/button'
import { Workflow } from '@trivie/core/src/models/workflow/Workflow'
import { color } from '@trivie/ui-web/theme'

import { ResourceSettings } from './components'
import { QuizSettings } from './components/quiz-settings'
import { TopicSettings } from './components/topic-settings'
import ContentCreationHeader from './content-creation-header'
import ContentCreationHeaderTitle from './content-creation-header-title'
import { ResourceUploadDialog } from './components/resource-upload-dialog'
import { StepPanel } from './components/step-drawer'
import { StepsList } from './components/steps-list'
import { ContentImportSidebar } from './content-import-sidebar'
import { BadgeApi, QuizApi, TopicApi } from '@trivie/core/src/services/api-objects'
import { Box, Stack } from '@mui/material'

export const CreateTopicScreen: React.FunctionComponent = observer(() => {
  const { contentCreationStore, pathStore } = useStores()
  const { workflow, loadWorkflow, path } = pathStore
  const { topics, importingFilename, selectedTopic, setTopics } = contentCreationStore
  const [showResourceDialog, setShowResourceDialog] = useState(false)

  const [showStepPanel, setShowStepPanel] = useState(false)

  const importing = useRouteMatch<any>('/content/creation/import')
  const history = useHistory()
  const { topicId } = useParams<any>()

  const handlePublish = async () => {
    history.push('/content/topics')
  }

  useEffect(() => {
    if (topicId && !selectedTopic && !topics?.length) {
      TopicHelper.loadTopic(Number(topicId))
        .then((topic: Instance<typeof Topic>) => {
          if (topic.workflow_id) {
            loadWorkflow(topic.workflow_id).then((wf) => {
              BadgeApi.getAllBadges(
                'users',
                false,
                undefined,
                'list_custom_badges',
                undefined,
                'custom',
              ).then((res) => {
                const bList = [] as any
                res.data?.rows.map((badge) => {
                  const badge_title = badge.columns.find((c) => c.field === 'badge_title')
                  const photo_url = badge.columns.find((c) => c.field === 'photo_url')
                  bList.push({ label: badge_title.value, photo_url: photo_url.value, id: badge.id })
                })
                wf?.actions.map((action) => {
                  if (action.action_type === 'award_badge') {
                    const found = bList.find((b) => b.id === action.object_id)
                    action.badge = found
                  }
                })
                pathStore.setWorkflow(wf)
              })
            })
          }
          setTopics([topic])
        })
        .catch((e) => console.warn(e))
    }
    if (!selectedTopic) {
      setTopics([TopicHelper.createNewTopic()])
    }
  }, [])

  const [topicNames, setTopicNames] = useState([])
  useEffect(() => {
    TopicApi.getAllTopics('topics', false, [{ field: 'topic_title' }], 'list_topics').then(
      (res: any) =>
        setTopicNames(
          res.data.rows.map((row) => {
            return row.columns
              .find((c) => c.field === 'topic_title')
              ?.value?.replace(/\s+/g, '')
              ?.toLowerCase()
          }),
        ),
    )
  }, [])

  const [existingQuizNames, setExistingQuizNames] = useState([])
  useEffect(() => {
    QuizApi.getAllQuizzes('quizzes', false, [{ field: 'quiz_title' }], 'list_quizzes').then(
      (res: any) =>
        setExistingQuizNames(
          res.data.rows.map((row) =>
            row.columns
              .find((c) => c.field === 'quiz_title')
              ?.value?.replace(/\s+/g, '')
              ?.toLowerCase(),
          ),
        ),
    )
  }, [])

  useEffect(() => {
    topics?.map((t) => {
      if (
        (t?.topic_id &&
          topicNames.includes(t?.title?.replace(/\s+/g, '')?.toLowerCase() as never) &&
          t?.title?.replace(/\s+/g, '')?.toLowerCase() !==
            t?.original_name?.replace(/\s+/g, '')?.toLowerCase()) ||
        (!t?.topic_id && topicNames.includes(t?.title?.replace(/\s+/g, '')?.toLowerCase() as never))
      ) {
        t?.set('duplicate_name', true)
      } else {
        t?.set('duplicate_name', false)
      }
      t.quizzes?.map((q) => {
        if (
          existingQuizNames &&
          existingQuizNames.includes(q?.title?.replace(/\s+/g, '')?.toLowerCase() as never) &&
          q?.title?.replace(/\s+/g, '')?.toLowerCase() !==
            q?.original_name?.replace(/\s+/g, '')?.toLowerCase()
        ) {
          q?.set('duplicate_name', true)
        } else {
          q?.set('duplicate_name', false)
        }
      })
    })
  }, [topicNames?.length, existingQuizNames?.length, selectedTopic?.title])

  useEffect(() => {
    if (selectedTopic?.workflow) {
      selectedTopic?.workflow.setName(selectedTopic?.title + ' Workflow')
    }
  }, [selectedTopic?.title])

  const handleAddStep = () => {
    setShowStepPanel(true)
    if (importing) {
      if (!selectedTopic?.workflow) {
        selectedTopic?.setWorkflow(
          Workflow.create({
            version: 1.1,
            name: selectedTopic?.title + ' Workflow',
          }),
        )
      }
      selectedTopic?.workflow?.setEditing('')
      selectedTopic?.workflow?.selectStep(undefined)
    } else {
      if (!workflow) {
        pathStore.setWorkflow(
          Workflow.create({
            version: 1.1,
            name: selectedTopic?.title + ' Workflow',
          }),
        )
      }
      if (workflow?.editing) {
        workflow?.setEditing('')
        workflow?.selectStep(undefined)
      }
    }
  }

  if (!selectedTopic) {
    return null
  }

  return (
    <Box component="div" style={ROOT}>
      <ContentCreationHeader
        onBack={handlePublish}
        showUnsavedPopup
        publishData={selectedTopic}
        titleHeader={
          <ContentCreationHeaderTitle
            titles={[
              {
                title: importing
                  ? `Importing - ${importingFilename}`
                  : selectedTopic.title
                    ? selectedTopic.title
                    : 'Untitled Topic',
                isPlaceholder: importingFilename || !selectedTopic.title,
              },
            ]}
          />
        }
      />
      {importing && topics ? (
        <div style={{ ...CONTENT, overflowY: 'hidden' }}>
          {topics && topics?.length > 1 && <ContentImportSidebar topics={topics} />}
          <div style={{ ...CONTAINER, overflowY: 'auto' }}>
            <Grid container spacing={2} style={{ width: '100%', maxWidth: 1400 }}>
              <Grid size={{ md: 12, lg: 5 }} rowGap={3}>
                <Stack direction="column" gap={2} useFlexGap>
                  <TopicSettings />
                  <ResourceSettings openResourceDialog={() => setShowResourceDialog(true)} />
                </Stack>
              </Grid>
              <Grid size={{ md: 12, lg: 7 }}>
                <Stack direction="column" gap={2} useFlexGap>
                  <QuizSettings existingQuizNames={existingQuizNames} />
                  {selectedTopic?.workflow && selectedTopic?.workflow.nodes.length > 0 ? (
                    <StepsList
                      workflow={selectedTopic?.workflow}
                      path={path}
                      showStepPanel={showStepPanel}
                      topic={selectedTopic}
                      handleAddStep={handleAddStep}
                    />
                  ) : (
                    <Button
                      sx={{ margin: '0 auto !important', marginBottom: '40px !important' }}
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={handleAddStep}
                      text="Add Award After Topic Completion"
                    />
                  )}
                </Stack>
              </Grid>
            </Grid>
          </div>
          <StepPanel
            importing
            topic={selectedTopic}
            workflow={workflow}
            open={showStepPanel && workflow}
            onOpen={() => setShowStepPanel(true)}
            onClose={() => setShowStepPanel(false)}
          />
        </div>
      ) : (
        <div style={CONTENT}>
          <div id="PathContainer" style={CONTAINER}>
            <Grid container spacing={2} style={{ width: '100%', maxWidth: 1400 }}>
              <Grid size={{ sm: 12, md: 5 }}>
                <Stack direction="column" gap={2} useFlexGap>
                  <TopicSettings />
                  <ResourceSettings openResourceDialog={() => setShowResourceDialog(true)} />
                </Stack>
              </Grid>
              <Grid size={{ sm: 12, md: 7 }}>
                <Stack direction="column" gap={2} useFlexGap>
                  <QuizSettings />
                  {workflow && workflow.nodes.length > 0 ? (
                    <StepsList
                      workflow={workflow}
                      path={path}
                      showStepPanel={showStepPanel}
                      topic={selectedTopic}
                      handleAddStep={handleAddStep}
                    />
                  ) : (
                    <Button
                      sx={{ margin: '0 auto !important', marginBottom: '40px !important' }}
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={handleAddStep}
                      text="Add Award After Topic Completion"
                    />
                  )}
                </Stack>
              </Grid>
            </Grid>
          </div>
          <StepPanel
            topic={selectedTopic}
            workflow={workflow}
            open={showStepPanel && workflow}
            onOpen={() => setShowStepPanel(true)}
            onClose={() => setShowStepPanel(false)}
          />
        </div>
      )}
      {showResourceDialog && (
        <ResourceUploadDialog
          topic={selectedTopic}
          close={() => setShowResourceDialog(false)}
          open={showResourceDialog}
        />
      )}
    </Box>
  )
})

const CONTAINER: React.CSSProperties = {
  padding: 40,
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
}
const ROOT: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  background: 'rgb(244, 244, 244)',
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
}
const CONTENT: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  height: '100%',
  overflowY: 'auto',
}
